<script setup lang="ts">
/**
 * Refund detail body — renders identically inside the drawer chrome OR
 * on a page wrapped in a `<Box padding="4">`. Props in, nothing else.
 *
 * Includes a "View order details →" link to the related order detail
 * page (admin's existing orders module).
 */
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import CopyButton from '@uiKit/components/CopyButton.vue'
import { formatDateTime, formatKey, formatMoney } from '@shared/utils'
import { getRefundMethodLabel } from '../utils/refundStatusUtils'
import type { RefundDetail } from '../types/refundsTypes'

const props = defineProps<{
  details: RefundDetail
}>()

const emit = defineEmits<{
  'open-order': [orderId: string]
}>()

const investorFullName = computed(() => {
  const first = props.details.investor.firstName ?? ''
  const last = props.details.investor.lastName ?? ''
  return `${first} ${last}`.trim() || '—'
})

const amountLabel = computed(() => formatMoney(props.details.currencyCode, props.details.amount))

const orderNetAmountLabel = computed(() =>
  formatMoney(props.details.order.currencyCode, props.details.order.netAmount),
)

const showAuditSection = computed(
  () =>
    !!props.details.audit.approvedBy ||
    !!props.details.audit.approvedAt ||
    !!props.details.audit.settledAt ||
    !!props.details.audit.settlementReference,
)

function openOrderDetail() {
  emit('open-order', props.details.order.id)
}
</script>

<template>
  <Box flex direction="col" gap="4">
    <Box flex direction="col" gap="3">
      <Box flex direction="col" gap="1">
        <Text type="caption" color="muted">Refund amount</Text>
        <Text type="display" weight="bold" style="font-variant-numeric: tabular-nums">
          {{ amountLabel }}
        </Text>
      </Box>

      <Box flex justify="between">
        <Text type="body-sm" color="muted">Requested</Text>
        <Text type="body-sm" weight="medium">{{ formatDateTime(details.requestedAt) }}</Text>
      </Box>

      <Box flex justify="between">
        <Text type="body-sm" color="muted">Method</Text>
        <Text type="body-sm" weight="medium">{{ getRefundMethodLabel(details.refundMethod) }}</Text>
      </Box>
    </Box>

    <Box class="section">
      <Text
        type="caption-sm"
        transform="uppercase"
        weight="semibold"
        color="muted"
        class="section-label"
        >Investor</Text
      >
      <Box flex direction="col" gap="2" class="rows">
        <Box flex justify="between">
          <Text type="body-sm" color="muted">Name</Text>
          <Text type="body-sm" weight="medium">{{ investorFullName }}</Text>
        </Box>
        <Box v-if="details.investor.email" flex justify="between">
          <Text type="body-sm" color="muted">Email</Text>
          <Text type="body-sm" weight="medium">{{ details.investor.email }}</Text>
        </Box>
        <Box v-if="details.investor.kycStatus" flex justify="between">
          <Text type="body-sm" color="muted">KYC</Text>
          <StatusBadge
            :color="details.investor.kycStatus === 'approved' ? 'success' : 'warning'"
            :label="formatKey(details.investor.kycStatus)"
          />
        </Box>
      </Box>
    </Box>

    <Box class="section">
      <Text
        type="caption-sm"
        transform="uppercase"
        weight="semibold"
        color="muted"
        class="section-label"
        >Related order</Text
      >
      <Box flex direction="col" gap="2" class="rows">
        <Box flex justify="between" align="center" gap="2">
          <Text type="body-sm" color="muted">Reference</Text>
          <Box flex align="center" gap="2">
            <Text type="body-sm" weight="medium">{{ details.order.orderReference || '—' }}</Text>
            <CopyButton
              v-if="details.order.orderReference"
              :value="details.order.orderReference"
              label="Copy"
              copied-label="Copied"
            />
          </Box>
        </Box>
        <Box v-if="details.order.propertyName" flex justify="between">
          <Text type="body-sm" color="muted">Property</Text>
          <Text type="body-sm" weight="medium">{{ details.order.propertyName }}</Text>
        </Box>
        <Box v-if="details.order.tokenQuantity !== null" flex justify="between">
          <Text type="body-sm" color="muted">Tokens</Text>
          <Text type="body-sm" weight="medium">{{ details.order.tokenQuantity }}</Text>
        </Box>
        <Box flex justify="between">
          <Text type="body-sm" color="muted">Original amount</Text>
          <Text type="body-sm" weight="medium" style="font-variant-numeric: tabular-nums">
            {{ orderNetAmountLabel }}
          </Text>
        </Box>
        <Box v-if="details.order.orderStatus" flex justify="between">
          <Text type="body-sm" color="muted">Order status</Text>
          <StatusBadge
            color="neutral"
            :label="details.order.cancelledAt ? 'Cancelled' : formatKey(details.order.orderStatus)"
          />
        </Box>
        <Box flex justify="between">
          <Text type="body-sm" color="muted">Order created</Text>
          <Text type="body-sm" weight="medium">{{ formatDateTime(details.order.createdAt) }}</Text>
        </Box>
        <Box v-if="details.order.rejectedAt" flex justify="between">
          <Text type="body-sm" color="muted">Order rejected</Text>
          <Text type="body-sm" weight="medium">{{ formatDateTime(details.order.rejectedAt) }}</Text>
        </Box>
        <Box flex justify="end" class="related-action">
          <Button
            label="View order details"
            icon="arrow-right"
            icon-pos="right"
            variant="text"
            color="primary"
            @click="openOrderDetail"
          />
        </Box>
      </Box>
    </Box>

    <Box v-if="details.notes" class="section">
      <Text
        type="caption-sm"
        transform="uppercase"
        weight="semibold"
        color="muted"
        class="section-label"
        >Reason / notes</Text
      >
      <Box class="notes-block" padding="3">
        <Text type="body-sm">{{ details.notes }}</Text>
      </Box>
    </Box>

    <Box v-if="showAuditSection" class="section">
      <Text
        type="caption-sm"
        transform="uppercase"
        weight="semibold"
        color="muted"
        class="section-label"
        >Audit</Text
      >
      <Box flex direction="col" gap="2" class="rows">
        <Box v-if="details.audit.approvedBy" flex justify="between">
          <Text type="body-sm" color="muted">Approved by</Text>
          <Text type="body-sm" weight="medium">{{ details.audit.approvedBy }}</Text>
        </Box>
        <Box v-if="details.audit.approvedAt" flex justify="between">
          <Text type="body-sm" color="muted">Approved at</Text>
          <Text type="body-sm" weight="medium">{{ formatDateTime(details.audit.approvedAt) }}</Text>
        </Box>
        <Box v-if="details.audit.settledAt" flex justify="between">
          <Text type="body-sm" color="muted">Settled at</Text>
          <Text type="body-sm" weight="medium">{{ formatDateTime(details.audit.settledAt) }}</Text>
        </Box>
        <Box v-if="details.audit.settlementReference" flex justify="between">
          <Text type="body-sm" color="muted">Settlement ref</Text>
          <Text type="body-sm" weight="medium">{{ details.audit.settlementReference }}</Text>
        </Box>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.section {
  border-top: 1px solid var(--p-content-border-color);
  padding-top: 1rem;
}
.section-label {
  display: block;
  margin-bottom: 0.5rem;
}
.rows {
  padding-top: 0.25rem;
}
.related-action {
  padding-top: 0.5rem;
}
.notes-block {
  background: var(--p-surface-50, var(--p-surface-100));
  border-radius: var(--p-border-radius-md);
}
</style>
